/**
 * useReminderScheduler
 *
 * Schedules a daily in-app browser notification at the exact time the user
 * chose in Settings → Reminder Time.
 *
 * Rules:
 *  - Only fires if `pushEnabled === true` AND Notification.permission === "granted"
 *  - Skips when `skipToday === true` (day marked, imported holiday, or goal met)
 *  - Fires every day that qualifies:
 *      • Weekend days are skipped unless `includeWeekends === true`
 *  - After each fire, schedules itself for the exact same time tomorrow.
 *  - Cleans up its own timeout on unmount / dependency change.
 *
 *  In-app notification rows: created once per day by the `send-smart-reminder`
 *  job (`DAILY_REMINDER` in DB, unique per user+UTC day). A client `INSERT` was
 *  removed — it overlapped the cron, doubled under React Strict Mode, and
 *  produced duplicate rows.
 *
 * Architecture note:
 *  This uses a recursive setTimeout (not setInterval) because we always
 *  target a specific wall-clock time. setInterval drift would compound over days.
 */

import { useEffect, useRef } from "react";

interface SchedulerOptions {
  reminderTime:    string;   // "HH:MM" 24h format stored in DB
  includeWeekends: boolean;
  pushEnabled:     boolean;
  /** When true, today's browser notification is suppressed. */
  skipToday?:      boolean;
}

export function useReminderScheduler({
  reminderTime,
  includeWeekends,
  pushEnabled,
  skipToday = false,
}: SchedulerOptions): void {
  // Keep a stable ref to the active timeout so we can clear it on re-render
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep skipToday in a ref so onFire always reads the latest value without
  // resetting the timer on every markedDays / goal-stats change.
  const skipTodayRef = useRef(skipToday);
  skipTodayRef.current = skipToday;

  useEffect(() => {
    // Preconditions: feature must be on and the browser must have permission
    if (!pushEnabled) return;
    if (!reminderTime) return;

    const [hStr, mStr] = reminderTime.split(":");
    const targetH = parseInt(hStr, 10);
    const targetM = parseInt(mStr ?? "0", 10);
    if (isNaN(targetH) || isNaN(targetM)) return;

    /** Returns ms until the next occurrence of HH:MM today or tomorrow */
    const msUntilNext = (): number => {
      const now  = new Date();
      const next = new Date(now);
      next.setHours(targetH, targetM, 0, 0);

      // If the target time has already passed today, aim for tomorrow
      if (next.getTime() <= now.getTime()) {
        next.setDate(next.getDate() + 1);
      }
      return next.getTime() - now.getTime();
    };

    /** Called each time the timer fires */
    const onFire = () => {
      const dayOfWeek = new Date().getDay(); // 0 = Sunday, 6 = Saturday
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

      if (!skipTodayRef.current && (!isWeekend || includeWeekends)) {
        if (typeof Notification !== "undefined" && Notification.permission === "granted") {
          try {
            const n = new Notification("Office Rhythm 🏢", {
              body: "Remember to log your office day!",
              icon: "/favicon.ico",
              tag:  "office-rhythm-daily-reminder", // de-dupes OS-level banners
              requireInteraction: false,
            });
            setTimeout(() => n.close(), 8_000);
          } catch {
            /* Some browsers block Notification constructor — silently ignore */
          }
        }
      }

      // Schedule tomorrow's reminder
      timerRef.current = setTimeout(onFire, msUntilNext());
    };

    // Kick off the first timer
    timerRef.current = setTimeout(onFire, msUntilNext());

    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [reminderTime, includeWeekends, pushEnabled]);
}
